import { useCallback, useRef } from 'react';

import { useAudioPipelineRecovery } from '@/hooks/useAudioPipelineRecovery';
import { useAudioQuality } from '@/hooks/useAudioQuality';
import { useConnectionStateToasts } from '@/hooks/useConnectionStateToasts';
import { useSilenceMonitor } from '@/hooks/useSilenceMonitor';
import { useStopRecorderOnUnmount } from '@/hooks/useStopRecorderOnUnmount';
import { useWakeLock } from '@/hooks/useWakeLock';
import { PCMAudioManager } from '@/lib/pcmAudioManager';
import type { ConnectionEvent, StreamingClient } from '@/lib/streamingClient';

interface UseStreamingRecorderOptions {
  /** Label for log messages, e.g. 'Dictation' */
  label: string;
  isRecording: boolean;
  isPaused: boolean;
  /** Pause handler for silence auto-pause */
  onAutoStop: () => void;
  /**
   * The session ended server-side and cannot be resumed.
   *
   * Pages use this to leave their recording state, because stopping the stream
   * is not enough on its own: the microphone keeps running and the UI keeps
   * saying "recording" while every chunk is dropped, so a clinician carries on
   * speaking into audio nobody will transcribe.
   */
  onSessionEnded?: (reason: string, tailMayBeIncomplete: boolean) => void;
  onMicrophoneError?: (error: unknown) => boolean;
  onMicrophoneReady?: () => void;
}

/**
 * The audio-capture half of a streaming recording, shared by the dictation,
 * conversation and embed pages: mic capture wired to the streaming client,
 * pause/flush/resume, and the safety nets around it (stall recovery, silence
 * monitor, connection toasts, wake lock, stop on unmount).
 *
 * Pages still own the session and the streaming client's event handlers; they
 * assign `streamingClientRef.current` before calling `startCapture`.
 */
export const useStreamingRecorder = ({
  label,
  isRecording,
  isPaused,
  onAutoStop,
  onSessionEnded,
  onMicrophoneError,
  onMicrophoneReady,
}: UseStreamingRecorderOptions) => {
  const pcmAudioManagerRef = useRef<PCMAudioManager | null>(null);
  const streamingClientRef = useRef<StreamingClient | null>(null);

  useStopRecorderOnUnmount(pcmAudioManagerRef);
  useWakeLock(isRecording && !isPaused);
  const { connectionState, handleConnectionStateChange: reportConnectionState } = useConnectionStateToasts();
  const {
    quality: audioQuality,
    bind: bindAudioQuality,
    markPending: markQualityPending,
  } = useAudioQuality({
    pcmAudioManagerRef,
    streamingClientRef,
    isRecording,
    isPaused,
    connected: connectionState === 'connected',
  });

  /**
   * Close the microphone the moment the server says the session is over.
   *
   * The streaming client stops SENDING on its own, but capture is owned here. A
   * page that is not told simply goes on recording into discarded audio.
   */
  const handleConnectionStateChange = useCallback(
    (event: ConnectionEvent) => {
      reportConnectionState(event);
      if (!event.terminal) return;
      console.warn(`[${label}] Session ended server-side (${event.reason}); stopping capture`);
      pcmAudioManagerRef.current?.stop();
      onSessionEnded?.(event.reason ?? 'session_ended', event.tailMayBeIncomplete ?? false);
    },
    [label, reportConnectionState, onSessionEnded]
  );
  const { pipelineStalled, handlePipelineStall, handleManualRecovery, resetRecoveryState } = useAudioPipelineRecovery({
    pcmAudioManagerRef,
    streamingClientRef,
    label,
    onMicrophoneError,
    onMicrophoneReady,
  });
  const silence = useSilenceMonitor({ pcmAudioManagerRef, streamingClientRef, label, isRecording, onAutoStop });
  const { handleSilenceStateChange, reset: resetSilenceMonitor } = silence;

  /** Open the mic and stream PCM to the current streaming client. */
  const startCapture = useCallback(
    async (deviceId?: string) => {
      const manager = new PCMAudioManager();
      pcmAudioManagerRef.current = manager;
      const client = streamingClientRef.current;
      if (client) bindAudioQuality(client, manager);
      manager.onStall(handlePipelineStall);
      resetRecoveryState();
      manager.onSilenceStateChange(handleSilenceStateChange);
      resetSilenceMonitor();
      await manager.start(deviceId || undefined, async (chunk) => {
        try {
          await streamingClientRef.current?.sendPCMChunk(chunk);
        } catch (error) {
          console.error(`[${label}] Error sending PCM chunk:`, error);
        }
      });
    },
    [label, handlePipelineStall, resetRecoveryState, handleSilenceStateChange, resetSilenceMonitor, bindAudioQuality]
  );

  /** Stop capture (keeping the graph for resume) and flush the server buffer so Phase 2 runs. */
  const pauseCapture = useCallback(async () => {
    pcmAudioManagerRef.current?.pause();
    try {
      await streamingClientRef.current?.flushBuffer();
    } catch (error) {
      // Continue the pause even if the flush fails — audio is already captured locally.
      console.error(`[${label}] Error flushing buffer on pause:`, error);
    }
  }, [label]);

  /**
   * Reconnect the paused capture graph — a fresh start() would leak the previous
   * mic stream. If there is no paused graph to resume (a failed stall recovery
   * leaves the manager stopped), rebuild capture instead, so the UI never shows
   * recording while nothing is captured. Throws if capture can't be restarted.
   */
  const resumeCapture = useCallback(
    async (deviceId?: string) => {
      const manager = pcmAudioManagerRef.current;
      const state = manager?.getState();
      if (state === 'paused' || state === 'recording' || state === 'recovering') {
        const client = streamingClientRef.current;
        if (client && manager) bindAudioQuality(client, manager);
        else markQualityPending();
        manager?.resume();
        resetSilenceMonitor();
        return;
      }
      console.warn(`[${label}] No paused capture to resume (state=${state ?? 'none'}); restarting capture`);
      await manager?.stop().catch(() => {});
      await startCapture(deviceId);
    },
    [label, resetSilenceMonitor, startCapture, bindAudioQuality, markQualityPending]
  );

  return {
    pcmAudioManagerRef,
    streamingClientRef,
    startCapture,
    pauseCapture,
    resumeCapture,
    connectionState,
    handleConnectionStateChange,
    pipelineStalled,
    handleManualRecovery,
    silence,
    audioQuality,
  };
};
